import { WORKFLOW_OPTION_SLOT } from "../../action-journey-studio/common/const/settings-const";
import { DOC_CAPTURE_DEFAULT_FILE_TYPES } from "../../action-journey-studio/common/const/doc-capture-const";

/**
 * The playbook authoring model.
 *
 * A playbook is a second way to author the same thing the drag-drop canvas
 * authors: a workflow that the runtime executes as an `stmDefinition`. Where the
 * canvas stores ReactFlow `nodes`/`edges` in `stm.metadata`, a playbook stores
 * this structure under `stm.metadata.playbook` and compiles it (see
 * ../compiler/compile-playbook.js) to exactly the same runtime format.
 *
 * Sections exist purely to group steps for the reader - "1. Order status",
 * "2. Cancellation flow". They carry no execution meaning; the compiler
 * flattens them into one chain.
 */

export const PLAYBOOK_MODEL_VERSION = 1;

// Marks which editor owns a journey. Written into `stm.metadata` on save so the
// playbook editor can refuse to open a canvas-authored workflow (and vice versa)
// rather than silently overwriting the other editor's model.
export const AUTHORING_MODE_PLAYBOOK = 'playbook';

/**
 * Which editor owns a record.
 *
 * Stored in two places, both written on save:
 *  - on the **action record** (`authoringMode`), which the actions list endpoint
 *    returns verbatim. This is what each list page filters on, so neither editor
 *    has to read every journey to decide what to show.
 *  - in **`stm.metadata.authoringMode`**, which the playbook editor checks when
 *    it opens one record.
 *
 * A record with no mode was made before playbooks existed, or by the canvas, so
 * it belongs to the workflow editor.
 */
export function isPlaybookRecord(record) {
    return record?.authoringMode === AUTHORING_MODE_PLAYBOOK;
}

/** The inverse: what the canvas workflow editor should list. */
export function isWorkflowRecord(record) {
    return !isPlaybookRecord(record);
}

export const STEP_KIND = {
    SEND: 'SEND',
    ASK: 'ASK',
    RUN: 'RUN',
    SET: 'SET',
    GO_TO: 'GO_TO',
    IF: 'IF',
    HANDOFF: 'HANDOFF',
    RUN_PLAYBOOK: 'RUN_PLAYBOOK',
};

/**
 * What a RUN step actually runs. The author picks a target from a dropdown of
 * the app's functions / APIs / knowledge bases; leaving it as PROMPT and typing
 * an instruction instead compiles to an agent action, which lets the agent work
 * out how to do it with the tools it already has.
 */
export const RUN_TARGET = {
    FUNCTION: 'FUNCTION',   // -> codehook
    API: 'API',             // -> webhook
    KNOWLEDGE: 'KNOWLEDGE', // -> docSearch
    PROMPT: 'PROMPT',       // -> agentAction
};

// Ordered as the picker shows them: the typed instruction is the default, the
// concrete tools follow.
export const RUN_TARGET_LABEL = {
    [RUN_TARGET.PROMPT]: 'Instruction',
    [RUN_TARGET.FUNCTION]: 'Function',
    [RUN_TARGET.API]: 'API',
    [RUN_TARGET.KNOWLEDGE]: 'Knowledge base',
};

/** How an ASK step collects its answer. */
export const ASK_INPUT = {
    TEXT: 'TEXT',
    BUTTONS: 'BUTTONS',
    CARDS: 'CARDS',
    DOCUMENT: 'DOCUMENT',
};

export const ASK_INPUT_LABEL = {
    [ASK_INPUT.TEXT]: 'Free text',
    [ASK_INPUT.BUTTONS]: 'Buttons',
    [ASK_INPUT.CARDS]: 'Cards',
    [ASK_INPUT.DOCUMENT]: 'Document upload',
};

/**
 * Presentation for each step kind: the verb badge in the row (text + background)
 * and the tone of the chip that follows it. Chip tones mirror the screenshot -
 * green for a variable, amber for a tool/target.
 */
export const STEP_KIND_META = {
    [STEP_KIND.SEND]: {
        verb: 'SEND',
        verbClass: 'text-orange-700',
        verbBg: 'bg-orange-50',
        tip: 'Send a message to the customer.',
        defaultLabel: 'Message the customer',
    },
    [STEP_KIND.ASK]: {
        verb: 'ASK',
        verbClass: 'text-blue-700',
        verbBg: 'bg-blue-50',
        tip: 'Ask the customer for something and store the answer in a variable.',
        defaultLabel: '',
    },
    [STEP_KIND.RUN]: {
        verb: 'RUN',
        verbClass: 'text-sky-700',
        verbBg: 'bg-sky-50',
        tip: 'Run a function, call an API, query a knowledge base, or give the agent an instruction.',
        defaultLabel: '',
    },
    [STEP_KIND.SET]: {
        verb: 'SET',
        verbClass: 'text-gray-700',
        verbBg: 'bg-gray-100',
        tip: 'Assign a value to a variable.',
        defaultLabel: '',
    },
    [STEP_KIND.GO_TO]: {
        verb: 'GO TO',
        verbClass: 'text-teal-700',
        verbBg: 'bg-teal-50',
        tip: 'Continue from another section in this playbook.',
        defaultLabel: '',
    },
    [STEP_KIND.IF]: {
        verb: 'IF',
        verbClass: 'text-purple-700',
        verbBg: 'bg-purple-50',
        tip: 'Branch using natural language or variable expressions.',
        defaultLabel: '',
    },
    [STEP_KIND.HANDOFF]: {
        verb: 'HANDOFF',
        verbClass: 'text-rose-700',
        verbBg: 'bg-rose-50',
        tip: 'Transfer the conversation to a person.',
        defaultLabel: 'Transfer to a human agent',
    },
    [STEP_KIND.RUN_PLAYBOOK]: {
        verb: 'PLAYBOOK',
        verbClass: 'text-sky-700',
        verbBg: 'bg-sky-50',
        tip: 'Run another playbook or workflow.',
        defaultLabel: '',
    },
};

/**
 * Ids for sections, steps and compiled states.
 *
 * Same scheme as the canvas's `createTimeBasedUniqueNumber`
 * (helpers/utils/object-util.js) - numeric-looking strings, since state ids end
 * up as keys in `stmDefinition.states`. Defined here rather than imported
 * because that module also pulls in nanoid, which is ESM-only and breaks under
 * this project's jest transform, and the compiler needs to stay unit-testable.
 */
let idCounter = 0;
export const newId = () => {
    idCounter += 1;
    // The random part is fixed-width: concatenating variable-width numbers makes
    // ids ambiguous (counter 1 + random 12 collides with counter 11 + random 2).
    const random = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    return `${Date.now() % 100000}${idCounter}${random}`;
};

/**
 * The starting config for each kind.
 *
 * These deliberately mirror the field names the runtime reads, and the toolbox's
 * own `defaultConfig` objects in
 * pages/action-journey-studio/toolbox/constants.js, so the compiler can hand
 * most of this straight through and the output stays schema-valid by
 * construction.
 */
export function defaultConfigFor(kind) {
    switch (kind) {
        case STEP_KIND.SEND:
            return { displayTextOptions: [] };

        case STEP_KIND.ASK:
            return {
                inputType: ASK_INPUT.TEXT,
                slot: '',
                displayTextOptions: [],
                validationErrorDisplayTextOptions: [],
                autoFillFromEntity: true,
                // buttons / cards
                data: [],
                optionsTitle: '',
                // document upload
                allowedFileTypes: [...DOC_CAPTURE_DEFAULT_FILE_TYPES],
                extractionSchema: '',
            };

        case STEP_KIND.SET:
            return { slotToAssign: '', value: '' };

        case STEP_KIND.RUN:
            return {
                // A RUN step starts as a typed instruction - the author writes
                // what they want done. Choosing a function/API/knowledge base
                // from the picker switches it to that tool instead.
                target: RUN_TARGET.PROMPT,
                // FUNCTION
                codehookId: '',
                defaultInput: '{}',
                // API
                webhookConfig: {
                    url: '',
                    method: 'POST',
                    parameters: [],
                    requestFormData: [],
                    requestBody: '{}',
                    requestType: 'raw',
                    headers: [],
                    responseMapping: [],
                },
                pauseAfterExecution: false,
                // KNOWLEDGE
                inputPrompt: '',
                filterKb: [],
                dataSourceFiles: [],
                topK: 20,
                readFile: false,
                systemPrompt: '',
                outputFormat: '',
                // PROMPT (agent action)
                action: '',
                // shared
                slotToAssign: '',
                skipNotify: true,
                responseModifyByAI: false, // FUNCTION / API, only when shown
            };

        case STEP_KIND.GO_TO:
            return { sectionId: '' };

        case STEP_KIND.IF:
            return {};

        case STEP_KIND.HANDOFF:
            return {};

        case STEP_KIND.RUN_PLAYBOOK:
            return { journeyId: '' };

        default:
            return {};
    }
}

export function createStep(kind = STEP_KIND.SEND) {
    const step = {
        id: newId(),
        kind,
        label: STEP_KIND_META[kind]?.defaultLabel || '',
        config: defaultConfigFor(kind),
    };

    // An IF starts with one empty branch so the row has something to show and
    // the author has somewhere to drop the first nested step.
    if (kind === STEP_KIND.IF) {
        step.branches = [createBranch()];
    }

    return step;
}

/** Switch ordinary step types without changing their position or identity. */
export function changeStepKind(step, kind) {
    if (step.kind === STEP_KIND.IF || kind === STEP_KIND.IF || !STEP_KIND_META[kind] || step.kind === kind) return step;
    const config = defaultConfigFor(kind);
    // SEND and ASK share message wording; retain it when switching between them.
    if ([STEP_KIND.SEND, STEP_KIND.ASK].includes(step.kind) && [STEP_KIND.SEND, STEP_KIND.ASK].includes(kind)) {
        config.displayTextOptions = step.config?.displayTextOptions || [];
    }
    return { ...step, kind, label: STEP_KIND_META[kind].defaultLabel, config };
}

export function createBranch(condition = '') {
    return { id: newId(), condition, steps: [] };
}

export function createSection(title = 'New section') {
    return { id: newId(), title, steps: [] };
}

export function createEmptyPlaybook() {
    return {
        version: PLAYBOOK_MODEL_VERSION,
        sections: [createSection('Section 1')],
    };
}

/**
 * The slot an ASK step writes to. Mirrors the runtime default: a prompt with no
 * declared slot falls back to `workflow_option`.
 */
export function askSlotOf(step) {
    return step?.config?.slot || WORKFLOW_OPTION_SLOT;
}

/**
 * The config patch for a RUN step's chosen knowledge bases: the ids, plus the
 * indexed file names a "read the whole file" search opens - built the same way
 * the canvas doc-search settings build them (`lookups.knowledgeList[].file`).
 * No ids means every knowledge base.
 */
export function knowledgeSelection(ids, knowledgeList) {
    const byId = new Map((knowledgeList || []).map(k => [k.id, k]));
    return {
        filterKb: ids,
        dataSourceFiles: ids.map(id => byId.get(id)?.file).filter(Boolean),
    };
}

/** Every step in the playbook, including those nested inside IF branches. */
export function walkSteps(playbook, visit) {
    const walk = (steps, ctx) => {
        (steps || []).forEach((step, index) => {
            visit(step, { ...ctx, index });
            (step.branches || []).forEach(branch => {
                walk(branch.steps, { ...ctx, branchId: branch.id, parentId: step.id });
            });
        });
    };

    (playbook?.sections || []).forEach(section => {
        walk(section.steps, { sectionId: section.id });
    });
}

/** Variable names the playbook writes to - used to warn about unknown slots. */
export function collectAssignedSlots(playbook) {
    const names = new Set();
    walkSteps(playbook, step => {
        if (step.kind === STEP_KIND.ASK && step.config?.slot) {
            names.add(step.config.slot);
        }
        if ((step.kind === STEP_KIND.SET || step.kind === STEP_KIND.RUN) && step.config?.slotToAssign) {
            names.add(step.config.slotToAssign);
        }
    });
    return Array.from(names);
}

// Match the workflow choice contract. Older playbook branches used only condition.
export const PLAYBOOK_CONDITION_OPERATORS = [
    'is', 'is not', 'greater than', 'greater than or equal', 'less than',
    'less than or equal', 'contains', 'not contains', 'starts with', 'ends with',
    'is empty', 'is not empty',
];
export const branchMode = branch => branch.conditionType || 'natural';
export const branchText = branch => branch.naturalLanguage ?? branch.condition ?? '';
export const describeBranch = branch => branchMode(branch) === 'else' ? 'Else' : branchMode(branch) === 'natural'
    ? branchText(branch)
    : (branch.expressionList || []).map(rule =>
        `${rule.variable || 'Variable'} ${rule.operator || 'is'} ${rule.value ?? ''}`.trim()
    ).join(` ${branch.operator || 'AND'} `);

export function appendBranch(branches, conditionType = 'natural') {
    if (conditionType === 'else' && branches.some(branch => branchMode(branch) === 'else')) return branches;
    const branch = { ...createBranch(), conditionType };
    const elseIndex = branches.findIndex(item => branchMode(item) === 'else');
    if (conditionType !== 'else' && elseIndex !== -1) return [...branches.slice(0, elseIndex), branch, ...branches.slice(elseIndex)];
    return [...branches, branch];
}
